import { Fragment, useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { canonicalJson, fitsInUrl, normalizeFilter } from '../query/codec'
import { describeCondition } from '../query/describe'
import type { Condition, Filter, Register } from '../query/types'
import { ConditionRow } from './ConditionRow'
import { completeCondition, toDraft, type DraftCondition } from './draftCondition'
import type { FilterOptions } from './useFilterOptions'

// The limits of the filter model (backend `query/schema.py`, ED-34).
const MAX_GROUPS = 10
const MAX_CONDITIONS = 20
/** How long the draft has to stay as it is before the number on "Toepassen" is asked for. */
const COUNT_DELAY_MS = 400

interface DraftRow {
  id: number
  condition: DraftCondition
}

interface DraftGroup {
  id: number
  rows: DraftRow[]
}

// Rows and groups are told apart by an id of their own, not by their place: taking one away from
// the middle must not hand its state (a search term, a half-typed date) to the next.
let lastId = 0
const newId = (): number => ++lastId
const emptyRow = (): DraftRow => ({ id: newId(), condition: { property: null, operator: null } })

function draftOf(filter: Filter): DraftGroup[] {
  return filter.groups.map((group) => ({ id: newId(), rows: group.map((condition) => ({ id: newId(), condition: toDraft(condition) })) }))
}

function Connector({ word }: { word: 'EN' | 'OF' }) {
  return <p className="text-xs font-semibold tracking-wide text-muted-foreground">{word}</p>
}

/**
 * "Geavanceerde filters" (design §5.2): groups of conditions. All conditions of a group must hold
 * (EN), a record only has to satisfy one group (OF), and the quick filters of the page hold on top
 * of that. "Toepassen" says how many records the draft finds; a condition that is not complete yet
 * is left out of that number and of what is applied. Each opening starts from the filter on the page.
 */
export function AdvancedFilterSheet({
  open,
  onOpenChange,
  register,
  filter,
  options,
  today,
  noun,
  count,
  onApply,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  register: Register
  /** The filter on the page: its quick filters stay, its groups are what is edited. */
  filter: Filter
  options: FilterOptions
  /** Today in Amsterdam as YYYY-MM-DD: the days a quick filter covers are worked out from it. */
  today: string
  /** "9 contacten", "1 contact". */
  noun: (count: number) => string
  /** How many records a filter finds. */
  count: (draft: Filter) => Promise<number>
  onApply: (next: Filter) => void
}) {
  const [groups, setGroups] = useState<DraftGroup[]>(() => draftOf(filter))
  // Opening it again starts over from the filter on the page, not from what was left in the sheet.
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) setGroups(draftOf(filter))
  }

  const complete = groups
    .map((group) => group.rows.map((row) => completeCondition(row.condition, register)).filter((condition): condition is Condition => condition !== null))
    .filter((conditions) => conditions.length > 0)
  const candidate = normalizeFilter({ quick: filter.quick, groups: complete })
  const key = canonicalJson(candidate)
  const fits = fitsInUrl(candidate)

  // The newest `count`: a caller that makes a new function on every render must not restart the wait.
  const latestCount = useRef(count)
  useEffect(() => {
    latestCount.current = count
  })

  // Only the answer for the draft as it is now is shown; an older one that comes in late is not.
  const [answer, setAnswer] = useState<{ key: string; total: number | null } | null>(null)
  useEffect(() => {
    if (!open) return
    let stale = false
    const timer = setTimeout(() => {
      latestCount.current(normalizeFilter(JSON.parse(key) as Partial<Filter>)).then(
        (total) => {
          if (!stale) setAnswer({ key, total })
        },
        () => {
          if (!stale) setAnswer({ key, total: null })
        },
      )
    }, COUNT_DELAY_MS)
    return () => {
      stale = true
      clearTimeout(timer)
    }
  }, [open, key])
  const total = answer?.key === key ? answer.total : null

  function addGroup() {
    const group: DraftGroup = { id: newId(), rows: [emptyRow()] }
    setGroups((previous) => (previous.length >= MAX_GROUPS ? previous : [...previous, group]))
  }

  function removeGroup(groupId: number) {
    setGroups((previous) => previous.filter((group) => group.id !== groupId))
  }

  function addRow(groupId: number) {
    const row = emptyRow()
    setGroups((previous) =>
      previous.map((group) => (group.id === groupId && group.rows.length < MAX_CONDITIONS ? { ...group, rows: [...group.rows, row] } : group)),
    )
  }

  function changeRow(groupId: number, rowId: number, condition: DraftCondition) {
    setGroups((previous) =>
      previous.map((group) =>
        group.id === groupId ? { ...group, rows: group.rows.map((row) => (row.id === rowId ? { ...row, condition } : row)) } : group,
      ),
    )
  }

  /** A group without conditions is nothing: it goes with its last one. */
  function removeRow(groupId: number, rowId: number) {
    setGroups((previous) =>
      previous
        .map((group) => (group.id === groupId ? { ...group, rows: group.rows.filter((row) => row.id !== rowId) } : group))
        .filter((group) => group.rows.length > 0),
    )
  }

  function apply() {
    onApply(candidate)
    onOpenChange(false)
  }

  // "2 contacten" without its number: "contacten".
  const plural = noun(2).replace(/^\d+\s*/, '')
  const quick = filter.quick
    .map((condition) => describeCondition(condition, register.properties.find((property) => property.key === condition.property), { names: options.names, today }))
    .join(', ')

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" size="lg" aria-describedby={undefined}>
        <SheetTitle className="text-lg">Geavanceerde filters</SheetTitle>
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
          <p className="text-sm">{`Toon ${plural} die voldoen aan:`}</p>
          {groups.map((group, index) => (
            <Fragment key={group.id}>
              {index > 0 ? <Connector word="OF" /> : null}
              <div role="group" aria-label={`Groep ${index + 1}`} className="flex flex-col gap-3 rounded-lg border border-border p-3">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold">{`Groep ${index + 1}`}</h3>
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeGroup(group.id)}>
                    Groep verwijderen
                  </Button>
                </div>
                {group.rows.map((row, at) => (
                  <Fragment key={row.id}>
                    {at > 0 ? <Connector word="EN" /> : null}
                    <ConditionRow
                      condition={row.condition}
                      register={register}
                      options={options}
                      onChange={(next) => changeRow(group.id, row.id, next)}
                      onRemove={() => removeRow(group.id, row.id)}
                    />
                  </Fragment>
                ))}
                <Button type="button" variant="outline" size="sm" className="self-start" disabled={group.rows.length >= MAX_CONDITIONS} onClick={() => addRow(group.id)}>
                  + Voorwaarde (EN)
                </Button>
              </div>
            </Fragment>
          ))}
          <Button type="button" variant="outline" size="sm" className="self-start" disabled={groups.length >= MAX_GROUPS} onClick={addGroup}>
            + Groep toevoegen (OF)
          </Button>
          {filter.quick.length > 0 ? <p className="text-sm text-muted-foreground">{`Snelfilters (${quick}) gelden daarnaast voor alle groepen.`}</p> : null}
        </div>
        {fits ? null : (
          <p role="alert" className="text-sm text-destructive">
            Dit filter is te groot om in een link op te nemen. Haal voorwaarden of waarden weg.
          </p>
        )}
        <div className="flex items-center justify-between gap-2 border-t border-border pt-3">
          <Button type="button" variant="ghost" onClick={() => setGroups([])}>
            Wis alles
          </Button>
          <Button type="button" disabled={!fits} onClick={apply}>
            {`Toepassen · ${total === null ? '…' : noun(total)}`}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
